/**
 * SettingsPage - Account settings with full user profile management
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { YStack, XStack, H1, H2, Text, Input } from 'tamagui';
import { GlassCard, MatrixButton } from '../components/base';
import { useTheme } from '../App';
import axios from 'axios';

interface SettingsPageProps {
  user: any;
  onLogout: () => void;
}

const API_URL = 'https://nings-api.beamer-labs.workers.dev';

const SettingsPage: React.FC<SettingsPageProps> = ({ user, onLogout }) => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admin status
  const [isAdmin, setIsAdmin] = useState(false);

  // Admin-only earnings check trigger
  const [triggeringEarnings, setTriggeringEarnings] = useState(false);
  const [earningsResult, setEarningsResult] = useState<any>(null);

  // Profile fields
  const [displayName, setDisplayName] = useState('');
  const [fullName, setFullName] = useState('');
  const [location, setLocation] = useState('');
  const [timezone, setTimezone] = useState('');

  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [notificationFrequency, setNotificationFrequency] = useState<'instant' | 'daily' | 'weekly'>('instant');
  const [preferredNotificationTime, setPreferredNotificationTime] = useState('09:00');

  // Investment profile
  const [experienceLevel, setExperienceLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('beginner');
  const [interestedSectors, setInterestedSectors] = useState<string[]>([]);
  const [portfolioSize, setPortfolioSize] = useState<'small' | 'medium' | 'large' | null>(null);

  // Load user settings and check admin status on mount
  useEffect(() => {
    // Only load if we have a user object with an ID
    if (user && user.id) {
      loadUserSettings();
      checkAdminStatus();
    } else {
      setLoading(false);
      setError('User information not available. Please try logging in again.');
    }
  }, [user]);

  const checkAdminStatus = async () => {
    const token = localStorage.getItem('nings_access_token');
    if (!token) return;

    try {
      const response = await axios.get(`${API_URL}/auth/me/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsAdmin(response.data.isAdmin || false);
    } catch (err) {
      setIsAdmin(false);
    }
  };

  const loadUserSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('nings_access_token');
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        const settings = response.data.settings;

        // Profile
        setDisplayName(settings.display_name || '');
        setFullName(settings.full_name || '');
        setLocation(settings.location || '');
        setTimezone(settings.timezone || '');

        // Notifications
        setEmailNotifications(settings.email_notifications ?? true);
        setNotificationFrequency(settings.notification_frequency || 'instant');
        setPreferredNotificationTime(settings.preferred_notification_time || '09:00');

        // Investment profile
        setExperienceLevel(settings.experience_level || 'beginner');
        setInterestedSectors(settings.interested_sectors || []);
        setPortfolioSize(settings.portfolio_size || null);
      }
    } catch (err: any) {
      console.error('Failed to load settings:', err);
      setError(err.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);

      const token = localStorage.getItem('nings_access_token');
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const settingsUpdate = {
        display_name: displayName || null,
        full_name: fullName || null,
        location: location || null,
        timezone: timezone || null,
        email_notifications: emailNotifications,
        notification_frequency: notificationFrequency,
        preferred_notification_time: preferredNotificationTime,
        experience_level: experienceLevel,
        interested_sectors: interestedSectors,
        portfolio_size: portfolioSize,
      };

      const response = await axios.put(`${API_URL}/settings`, settingsUpdate, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err: any) {
      console.error('Failed to save settings:', err);
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleSector = (sector: string) => {
    setInterestedSectors((prev) =>
      prev.includes(sector) ? prev.filter((s) => s !== sector) : [...prev, sector]
    );
  };

  const handleTriggerEarningsCheck = async () => {
    try {
      setTriggeringEarnings(true);
      setEarningsResult(null);
      setError(null);

      const token = localStorage.getItem('nings_access_token');
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await axios.post(
        `${API_URL}/admin/trigger-earnings-check?days_back=1`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setEarningsResult(response.data);
    } catch (err: any) {
      console.error('Failed to trigger earnings check:', err);
      setError(err.response?.data?.detail || 'Failed to trigger earnings check');
    } finally {
      setTriggeringEarnings(false);
    }
  };

  const availableSectors = [
    'Technology',
    'Healthcare',
    'Finance',
    'Energy',
    'Consumer',
    'Industrial',
    'Real Estate',
    'Materials',
  ];

  const timezones = [
    { label: 'Pacific Time (PT)', value: 'America/Los_Angeles' },
    { label: 'Mountain Time (MT)', value: 'America/Denver' },
    { label: 'Central Time (CT)', value: 'America/Chicago' },
    { label: 'Eastern Time (ET)', value: 'America/New_York' },
    { label: 'Alaska Time (AKT)', value: 'America/Anchorage' },
    { label: 'Hawaii Time (HT)', value: 'Pacific/Honolulu' },
    { label: 'UTC', value: 'UTC' },
    { label: 'London (GMT)', value: 'Europe/London' },
    { label: 'Paris (CET)', value: 'Europe/Paris' },
    { label: 'Tokyo (JST)', value: 'Asia/Tokyo' },
    { label: 'Sydney (AEST)', value: 'Australia/Sydney' },
    { label: 'Hong Kong (HKT)', value: 'Asia/Hong_Kong' },
  ];

  const countries = [
    'United States',
    'Canada',
    'United Kingdom',
    'Australia',
    'Germany',
    'France',
    'Japan',
    'China',
    'India',
    'Singapore',
    'Other',
  ];

  if (loading) {
    return (
      <YStack flex={1} padding="$4" gap="$6" maxWidth={900} marginHorizontal="auto" width="100%" justifyContent="center" alignItems="center">
        <Text color="$green10" fontFamily="$mono" fontSize="$5">
          Loading settings...
        </Text>
      </YStack>
    );
  }

  return (
    <YStack flex={1} padding="$4" gap="$6" maxWidth={900} marginHorizontal="auto" width="100%">
      {/* Header */}
      <YStack gap="$2" alignItems="center" paddingVertical="$4">
        <H1 color="$green10" fontFamily="$heading" textAlign="center">
          Settings
        </H1>
        <Text color="$colorPress" fontFamily="$body" fontSize="$4" textAlign="center">
          Manage your account preferences
        </Text>
      </YStack>

      {/* Error Message */}
      {error && (
        <GlassCard borderColor="$red10" borderWidth={2}>
          <Text color="$red10" fontFamily="$body" fontSize="$3">
            ⚠️ {error}
          </Text>
        </GlassCard>
      )}

      {/* Account Information */}
      <GlassCard>
        <YStack gap="$4">
          <H2 color="$green10" fontFamily="$heading" fontSize="$5">
            Account Information
          </H2>
          <YStack gap="$3">
            <YStack gap="$2">
              <Text
                fontSize="$2"
                fontWeight="500"
                color="$placeholderColor"
                fontFamily="$mono"
                textTransform="uppercase"
              >
                Email
              </Text>
              <Text fontSize="$4" color="$color" fontFamily="$body">
                {user?.email || user?.emails?.[0] || 'Not available'}
              </Text>
            </YStack>
            <YStack gap="$2">
              <Text
                fontSize="$2"
                fontWeight="500"
                color="$placeholderColor"
                fontFamily="$mono"
                textTransform="uppercase"
              >
                User ID
              </Text>
              <Text fontSize="$3" color="$colorPress" fontFamily="$mono">
                {user?.id || 'Not available'}
              </Text>
            </YStack>
          </YStack>
        </YStack>
      </GlassCard>

      {/* Profile */}
      <GlassCard>
        <YStack gap="$4">
          <H2 color="$green10" fontFamily="$heading" fontSize="$5">
            Profile
          </H2>
          <YStack gap="$3">
            <YStack gap="$2">
              <Text fontSize="$3" fontWeight="500" color="$color" fontFamily="$body">
                Display Name
              </Text>
              <Input
                value={displayName}
                onChangeText={setDisplayName}
                placeholder="How should we call you?"
                fontFamily="$body"
                fontSize="$4"
                color="$color"
                backgroundColor="$backgroundHover"
                borderColor="$borderColor"
                placeholderTextColor="$placeholderColor"
              />
            </YStack>

            <YStack gap="$2">
              <Text fontSize="$3" fontWeight="500" color="$color" fontFamily="$body">
                Full Name
              </Text>
              <Input
                value={fullName}
                onChangeText={setFullName}
                placeholder="Your full name (optional)"
                fontFamily="$body"
                fontSize="$4"
                color="$color"
                backgroundColor="$backgroundHover"
                borderColor="$borderColor"
                placeholderTextColor="$placeholderColor"
              />
            </YStack>

            <YStack gap="$2">
              <Text fontSize="$3" fontWeight="500" color="$color" fontFamily="$body">
                Country
              </Text>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                style={{
                  fontFamily: 'inherit',
                  fontSize: '16px',
                  padding: '12px',
                  backgroundColor: '#1a1a1a',
                  color: '#e0e0e0',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                <option value="">Select a country</option>
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </YStack>

            <YStack gap="$2">
              <Text fontSize="$3" fontWeight="500" color="$color" fontFamily="$body">
                Timezone
              </Text>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                style={{
                  fontFamily: 'inherit',
                  fontSize: '16px',
                  padding: '12px',
                  backgroundColor: '#1a1a1a',
                  color: '#e0e0e0',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                <option value="">Select a timezone</option>
                {timezones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
              <Text fontSize="$2" color="$placeholderColor" fontFamily="$body">
                Used for earnings call times
              </Text>
            </YStack>
          </YStack>
        </YStack>
      </GlassCard>

      {/* Investment Profile */}
      <GlassCard>
        <YStack gap="$4">
          <H2 color="$green10" fontFamily="$heading" fontSize="$5">
            Investment Profile
          </H2>

          <YStack gap="$3">
            <YStack gap="$2">
              <Text fontSize="$3" fontWeight="500" color="$color" fontFamily="$body">
                Experience Level
              </Text>
              <XStack gap="$3" flexWrap="wrap">
                <MatrixButton
                  variant={experienceLevel === 'beginner' ? 'primary' : 'secondary'}
                  onPress={() => setExperienceLevel('beginner')}
                  flex={1}
                  minWidth={100}
                >
                  Beginner
                </MatrixButton>
                <MatrixButton
                  variant={experienceLevel === 'intermediate' ? 'primary' : 'secondary'}
                  onPress={() => setExperienceLevel('intermediate')}
                  flex={1}
                  minWidth={100}
                >
                  Intermediate
                </MatrixButton>
                <MatrixButton
                  variant={experienceLevel === 'advanced' ? 'primary' : 'secondary'}
                  onPress={() => setExperienceLevel('advanced')}
                  flex={1}
                  minWidth={100}
                >
                  Advanced
                </MatrixButton>
              </XStack>
            </YStack>

            <YStack gap="$2">
              <Text fontSize="$3" fontWeight="500" color="$color" fontFamily="$body">
                Interested Sectors
              </Text>
              <Text fontSize="$2" color="$placeholderColor" fontFamily="$body">
                Select sectors you're interested in
              </Text>
              <XStack gap="$2" flexWrap="wrap">
                {availableSectors.map((sector) => (
                  <MatrixButton
                    key={sector}
                    variant={interestedSectors.includes(sector) ? 'primary' : 'secondary'}
                    onPress={() => toggleSector(sector)}
                    size="small"
                  >
                    {sector}
                  </MatrixButton>
                ))}
              </XStack>
            </YStack>

            <YStack gap="$2">
              <Text fontSize="$3" fontWeight="500" color="$color" fontFamily="$body">
                Portfolio Size (Optional)
              </Text>
              <XStack gap="$3" flexWrap="wrap">
                <MatrixButton
                  variant={portfolioSize === 'small' ? 'primary' : 'secondary'}
                  onPress={() => setPortfolioSize(portfolioSize === 'small' ? null : 'small')}
                  flex={1}
                  minWidth={100}
                >
                  Small
                </MatrixButton>
                <MatrixButton
                  variant={portfolioSize === 'medium' ? 'primary' : 'secondary'}
                  onPress={() => setPortfolioSize(portfolioSize === 'medium' ? null : 'medium')}
                  flex={1}
                  minWidth={100}
                >
                  Medium
                </MatrixButton>
                <MatrixButton
                  variant={portfolioSize === 'large' ? 'primary' : 'secondary'}
                  onPress={() => setPortfolioSize(portfolioSize === 'large' ? null : 'large')}
                  flex={1}
                  minWidth={100}
                >
                  Large
                </MatrixButton>
              </XStack>
            </YStack>
          </YStack>
        </YStack>
      </GlassCard>

      {/* Notification Preferences */}
      <GlassCard>
        <YStack gap="$4">
          <H2 color="$green10" fontFamily="$heading" fontSize="$5">
            Notification Preferences
          </H2>

          {/* Email notifications toggle */}
          <YStack gap="$2">
            <XStack gap="$3" alignItems="center">
              <input
                type="checkbox"
                id="email-notifications"
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
                style={{
                  width: '24px',
                  height: '24px',
                  cursor: 'pointer',
                  accentColor: '#7dd3c0',
                }}
              />
              <Text
                fontSize="$4"
                color="$color"
                fontFamily="$body"
                cursor="pointer"
                onPress={() => setEmailNotifications(!emailNotifications)}
                flex={1}
              >
                Email notifications for earnings updates
              </Text>
            </XStack>
            <Text fontSize="$2" color="$placeholderColor" fontFamily="$body" paddingLeft="$9">
              Receive email alerts when companies you follow release earnings
            </Text>
          </YStack>

          {/* Notification frequency */}
          <YStack gap="$2">
            <Text fontSize="$3" fontWeight="500" color="$color" fontFamily="$body">
              Notification Frequency
            </Text>
            <XStack gap="$3" flexWrap="wrap">
              <MatrixButton
                variant={notificationFrequency === 'instant' ? 'primary' : 'secondary'}
                onPress={() => setNotificationFrequency('instant')}
                flex={1}
                minWidth={100}
              >
                Instant
              </MatrixButton>
              <MatrixButton
                variant={notificationFrequency === 'daily' ? 'primary' : 'secondary'}
                onPress={() => setNotificationFrequency('daily')}
                flex={1}
                minWidth={100}
              >
                Daily Digest
              </MatrixButton>
              <MatrixButton
                variant={notificationFrequency === 'weekly' ? 'primary' : 'secondary'}
                onPress={() => setNotificationFrequency('weekly')}
                flex={1}
                minWidth={100}
              >
                Weekly Digest
              </MatrixButton>
            </XStack>
          </YStack>

          {/* Preferred notification time */}
          {(notificationFrequency === 'daily' || notificationFrequency === 'weekly') && (
            <YStack gap="$2">
              <Text fontSize="$3" fontWeight="500" color="$color" fontFamily="$body">
                Preferred Time for Digest
              </Text>
              <Input
                value={preferredNotificationTime}
                onChangeText={setPreferredNotificationTime}
                placeholder="09:00"
                fontFamily="$mono"
                fontSize="$4"
                color="$color"
                backgroundColor="$backgroundHover"
                borderColor="$borderColor"
                placeholderTextColor="$placeholderColor"
                maxWidth={150}
              />
              <Text fontSize="$2" color="$placeholderColor" fontFamily="$body">
                24-hour format (e.g., 09:00, 14:30)
              </Text>
            </YStack>
          )}
        </YStack>
      </GlassCard>

      {/* Appearance */}
      <GlassCard>
        <YStack gap="$4">
          <H2 color="$green10" fontFamily="$heading" fontSize="$5">
            Appearance
          </H2>
          <YStack gap="$3">
            <YStack gap="$2">
              <Text fontSize="$3" fontWeight="500" color="$color" fontFamily="$body">
                Theme
              </Text>
              <Text fontSize="$2" color="$placeholderColor" fontFamily="$body">
                Choose your preferred color scheme
              </Text>
            </YStack>
            <XStack gap="$3" flexWrap="wrap">
              <MatrixButton
                variant={theme === 'dark' ? 'primary' : 'secondary'}
                onPress={() => theme === 'light' && toggleTheme()}
                flex={1}
                minWidth={120}
              >
                🌙 Dark Mode
              </MatrixButton>
              <MatrixButton
                variant={theme === 'light' ? 'primary' : 'secondary'}
                onPress={() => theme === 'dark' && toggleTheme()}
                flex={1}
                minWidth={120}
              >
                ☀️ Light Mode
              </MatrixButton>
            </XStack>
          </YStack>
        </YStack>
      </GlassCard>

      {/* Save Button */}
      <GlassCard backgroundColor="$backgroundHover">
        <XStack gap="$3" alignItems="center" flexWrap="wrap" justifyContent="space-between">
          <MatrixButton
            variant="primary"
            onPress={handleSaveSettings}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </MatrixButton>
          {saved && (
            <XStack gap="$2" alignItems="center">
              <Text fontSize="$5" color="$green10">
                ✓
              </Text>
              <Text fontSize="$3" color="$green10" fontFamily="$mono">
                Settings saved!
              </Text>
            </XStack>
          )}
        </XStack>
      </GlassCard>

      {/* Admin Section - Only for users with nings-admin role */}
      {isAdmin && (
        <GlassCard borderColor="$cyan10" borderWidth={2} backgroundColor="$backgroundPress">
          <YStack gap="$4">
            <YStack gap="$2">
              <H2 color="$cyan10" fontFamily="$heading" fontSize="$5">
                Admin Controls
              </H2>
              <Text fontSize="$3" color="$placeholderColor" fontFamily="$body">
                Admin-only features for testing and debugging
              </Text>
            </YStack>

            {/* Admin Dashboard Link */}
            <YStack gap="$2">
              <Text fontSize="$3" fontWeight="500" color="$color" fontFamily="$body">
                Admin Dashboard
              </Text>
              <Text fontSize="$2" color="$placeholderColor" fontFamily="$body">
                View metrics, user activity, and system health
              </Text>
              <MatrixButton variant="primary" onPress={() => navigate('/admin')}>
                Open Dashboard
              </MatrixButton>
            </YStack>

            <YStack gap="$3">
              <YStack gap="$2">
                <Text fontSize="$3" fontWeight="500" color="$color" fontFamily="$body">
                  Manually Trigger Earnings Check
                </Text>
                <Text fontSize="$2" color="$placeholderColor" fontFamily="$body">
                  Check last 24 hours for completed earnings and send digests
                </Text>
              </YStack>

              <MatrixButton
                variant="secondary"
                onPress={handleTriggerEarningsCheck}
                disabled={triggeringEarnings}
              >
                {triggeringEarnings ? 'Checking earnings...' : 'Trigger Earnings Check'}
              </MatrixButton>

              {earningsResult && (
                <GlassCard backgroundColor="$backgroundHover" borderColor={earningsResult.success ? "$green10" : "$red10"} borderWidth={1}>
                  <YStack gap="$2">
                    <Text fontSize="$3" color={earningsResult.success ? "$green10" : "$red10"} fontFamily="$mono" fontWeight="600">
                      {earningsResult.success ? 'Success' : 'Failed'}
                    </Text>
                    <Text fontSize="$2" color="$color" fontFamily="$body">
                      Users notified: {earningsResult.users_notified || 0}
                    </Text>
                    <Text fontSize="$2" color="$color" fontFamily="$body">
                      Tickers with earnings: {earningsResult.tickers_with_earnings?.length || 0}
                    </Text>
                    {earningsResult.tickers_with_earnings?.length > 0 && (
                      <Text fontSize="$2" color="$colorPress" fontFamily="$mono">
                        {earningsResult.tickers_with_earnings.join(', ')}
                      </Text>
                    )}
                    <Text fontSize="$2" color="$color" fontFamily="$body">
                      Summaries generated: {earningsResult.summaries_generated || 0}
                    </Text>
                    <Text fontSize="$2" color="$color" fontFamily="$body">
                      Emails sent: {earningsResult.emails_sent || 0}
                    </Text>
                    {earningsResult.error && (
                      <Text fontSize="$2" color="$red10" fontFamily="$mono">
                        Error: {earningsResult.error}
                      </Text>
                    )}
                  </YStack>
                </GlassCard>
              )}
            </YStack>
          </YStack>
        </GlassCard>
      )}

      {/* Danger Zone */}
      <GlassCard borderColor="$red10" borderWidth={2} backgroundColor="$backgroundPress">
        <YStack gap="$4">
          <YStack gap="$2">
            <H2 color="$red10" fontFamily="$heading" fontSize="$5">
              Account Actions
            </H2>
            <Text fontSize="$3" color="$placeholderColor" fontFamily="$body">
              Careful — these actions affect your account
            </Text>
          </YStack>
          <MatrixButton variant="secondary" onPress={onLogout}>
            Logout
          </MatrixButton>
        </YStack>
      </GlassCard>
    </YStack>
  );
};

export default SettingsPage;
